// ab-tracking.js — shared tracking core for the new-PDP A/B test.
//
// Three jobs, in this order of importance:
//
//   1. Tell GA4 which arm this visitor is in, as a USER PROPERTY. This is the keystone of the whole
//      measurement plan. An event-scoped parameter only labels the events we write ourselves; GA4's
//      ecommerce events (view_item, add_to_cart, purchase) come from Shopify's Google & YouTube app
//      pixel, which we do not author and cannot attach a parameter to. A user property attaches to
//      the VISITOR, so every event they fire afterwards inherits it — including the ones we never
//      wrote. Without it, no standard GA4 report can be split by arm.
//
//   2. Stamp the arm onto the cart, so it reaches the order. Rollouts theme experiments report no
//      revenue and no AOV at all, so revenue-per-arm has to come from Shopify's own order records.
//      A Flow workflow turns this attribute into an order tag.
//
//   3. Expose ssTrack / ssTrackOnce / ssOnLeave for the per-arm wiring files.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// THE TIMING TRAP, and why everything is queued.
//
// GA4 here is configured by the Google & YouTube app, and that config arrives inside
// content_for_header. An event fired before the config lands reaches the dataLayer with no property
// configured to receive it and is SILENTLY DROPPED. There is no error and no warning.
//
// Worse, the obvious guard is useless: `typeof gtag === 'function'` passes long before the config
// exists, because gtag() is just a dataLayer shim. So a passing guard is not proof the event landed.
// This cost real time on the Sundae Flowers test — see docs there.
//
// So: nothing sends until DOMContentLoaded, everything before that is queued, and if gtag still is
// not there we poll briefly rather than dropping the queue on the floor. The poll is belt-and-braces
// — DOMContentLoaded alone was sufficient on Flowers — but the Google app's script is async, and a
// bounded retry costs nothing when it is already working.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// gtag('set', {...}) DOES NOT WORK for event parameters here.
//
// Verified on Flowers: a bare gtag('set', { ab_variant: … }) does NOT propagate to a config owned by
// the app — the event arrives carrying only its six automatic parameters. A parameter passed
// DIRECTLY on the event does arrive. gtag('set', 'user_properties', {...}) is a different call and
// does work. Hence: user properties via set, everything else passed inline on each event.
//
// Plan: docs/AB TEST/PDP/new-pdp-ab-test-plan.md

(function () {
  'use strict';

  var CFG = window.SS_AB;
  if (!CFG || !CFG.variant) return;

  var VARIANT = CFG.variant;

  // ── send path ──────────────────────────────────────────────────────────────────────────────
  //
  // `ready` flips once the user property has been set, so queued events inherit it rather than
  // arriving unlabelled. Order matters: set the property, THEN flush.

  var ready = false;
  var queue = [];

  function send(name, params) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', name, params || {});
  }

  function flush() {
    ready = true;
    for (var i = 0; i < queue.length; i++) send(queue[i][0], queue[i][1]);
    queue.length = 0;
  }

  // Public: fire an event. Safe to call at any time, from any file, before or after GA4 is ready.
  function ssTrack(name, params) {
    if (!name) return;
    if (ready) send(name, params);
    else queue.push([name, params]);
  }

  // Public: fire at most once per page view. The wiring files use this for every impression and
  // every "reached" event — an IntersectionObserver can fire repeatedly as the user scrolls back
  // and forth, and a scroll landmark counted twice is not a landmark.
  var fired = {};
  function ssTrackOnce(key, name, params) {
    if (!key || fired[key]) return;
    fired[key] = true;
    // Through window.* deliberately: calling the local ssTrack here would make every once-per-view
    // event invisible to a console wrapper on window.ssTrack, which is exactly how you debug this.
    window.ssTrack(name, params);
  }

  // Public: run fn once, when the visitor leaves — tab hidden, or page unloading.
  //
  // WHY THIS EXISTS. The two duration/depth metrics (time in the zoom gallery, how far through the
  // mobile gallery someone got) are only known when the visitor is finished. But most people never
  // "finish" explicitly — they navigate away, hit back, or switch apps. Listening only for a close
  // button loses exactly the longest, most engaged sessions, which is the opposite of what we want.
  //
  // visibilitychange→hidden is the reliable signal on mobile; pagehide covers desktop navigation and
  // bfcache. Both can fire, hence the once-guard.
  var leaveCbs = [];
  var left = false;
  function runLeave() {
    if (left) return;
    left = true;
    for (var i = 0; i < leaveCbs.length; i++) {
      try { leaveCbs[i](); } catch (e) { /* one bad callback must not block the others */ }
    }
  }
  function ssOnLeave(fn) {
    if (typeof fn === 'function') leaveCbs.push(fn);
  }
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') runLeave();
  });
  window.addEventListener('pagehide', runLeave);

  window.ssTrack = ssTrack;
  window.ssTrackOnce = ssTrackOnce;
  window.ssOnLeave = ssOnLeave;

  // ── cart attribute — "approach C" ──────────────────────────────────────────────────────────
  //
  // Liquid has already read cart.attributes.ab_test and handed it over in SS_AB.cartAttr. If it already
  // matches this theme's label, there is nothing to do — no request on the vast majority of page
  // views.
  //
  // If it is missing or WRONG, write it on the visitor's first interaction. Two reasons not to write
  // on page load: it would create a cart for every bot and every bounce, and it would race the cart
  // apps that are also touching /cart on load.
  //
  // A wrong value is not an error case — it is a visitor who was re-bucketed, or who had a cart from
  // before the test. Overwriting is the correct, self-healing behaviour.
  //
  // Deliberately NOT hooked to add-to-cart: this must not depend on how UpCart, Kaching or a quick
  // view happens to add items. It runs off plain user interaction and is independent of all of them.

  function writeCartAttribute() {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { ab_test: VARIANT } })
    }).catch(function () { /* tracking must never break the page */ });
  }

  if (CFG.cartAttr !== VARIANT) {
    var opts = { once: true, passive: true, capture: true };
    var armed = true;
    function onFirstInteraction() {
      if (!armed) return;
      armed = false;
      writeCartAttribute();
    }
    ['pointerdown', 'touchstart', 'keydown'].forEach(function (evt) {
      document.addEventListener(evt, onFirstInteraction, opts);
    });
  }

  // ── GA4 handshake ──────────────────────────────────────────────────────────────────────────

  // IS GA4 ACTUALLY CONFIGURED YET — not merely "does gtag() exist".
  //
  // This distinction is the whole ballgame, and getting it wrong cost us a debugging session.
  // `typeof gtag === 'function'` is true almost immediately, because gtag() is just a shim that
  // pushes onto dataLayer. But an event pushed BEFORE the Google & YouTube app's config command has
  // been processed is silently discarded — no error, no warning, and the guard still passes.
  //
  // The symptom was diagnostic: events fired at DOMContentLoaded (ab_variant_assigned, and the user
  // property) mostly vanished, while events fired on user interaction seconds later all arrived.
  // ab_variant_assigned should fire once per page view; it was landing perhaps once in five.
  //
  // The reliable signal is google_tag_manager["G-…"], which gtag.js creates only once the container
  // has actually initialised — verified on the live storefront, alongside the config commands in
  // dataLayer. Matched on the G- prefix rather than a hardcoded ID so this survives a property change.
  function ga4Ready() {
    if (typeof window.gtag !== 'function') return false;
    var gtm = window.google_tag_manager;
    if (!gtm) return false;
    for (var key in gtm) {
      if (key.indexOf('G-') === 0) return true;
    }
    return false;
  }

  function handshake() {
    // User property FIRST — it applies to events fired after it is set, so anything queued must
    // flush after. Known and accepted limitation: the session's first page_view has already gone
    // by this point and will not carry it. GA4 resolves user-scoped dimensions at the USER level,
    // so reports are still correct; only raw per-event counts show the one-hit discrepancy.
    window.gtag('set', 'user_properties', { ab_variant: VARIANT });

    // The per-hit record. Also the sample-ratio-mismatch check: counting this event per arm is
    // how we confirm the traffic split actually landed where Rollouts was told to put it.
    send('ab_variant_assigned', { ab_variant: VARIANT });

    flush();
  }

  function start() {
    var tries = 0;
    (function attempt() {
      if (ga4Ready()) { handshake(); return; }
      // ~10s ceiling. Longer than feels necessary because this runs against a third-party script
      // on a page carrying a dozen apps, and a late handshake still produces correct data whereas
      // an early one produces none.
      if (tries++ < 50) { setTimeout(attempt, 200); return; }
      // Last resort: send anyway rather than lose the arm label entirely. A possibly-dropped event
      // beats a guaranteed-missing one, and the queued interaction events still need flushing.
      if (typeof window.gtag === 'function') handshake();
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
